/* tempexp_18086-start */
import type { Space } from '@mail/cross-sizes-utils';
import { classNames } from '@vkontakte/vkui';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { REFERRAL_ANALYTIC_NAME, referralProgramMaxQuota } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import type { BillingReferralProgram } from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { renderReferralUserModal } from 'reactApp/ui/ReferralProgram/ReferralUserModal/ReferralUserModal.helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { ReferralInfoBlock } from './InfoBlock/ReferralInfoBlock';
import { ReferralLinkBlock } from './LinkBlock/ReferralLinkBlock';
import styles from './ReferralModal.css';
import { closeReferralModal } from './ReferralModal.helpers';
import { ReferralUsersBlock } from './UsersBlock/ReferralUsersBlock';

export interface ReferralModalProps {
    isReturn: boolean;
    quota: Space;
    list: BillingReferralProgram[];
    onClose?(): void;
}

export function ReferralModal({ onClose, quota, list, isReturn }: ReferralModalProps) {
    const isMaxQuota = quota.original === referralProgramMaxQuota.original;
    const isMobile = useSelector(EnvironmentSelectors.isMobile);
    useEffect(() => {
        if (!isReturn) {
            emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_MODAL_SHOW);
            sendPaymentGa({
                action: 'show',
                eventCategory: ECategoryGa.referalModal,
                name: REFERRAL_ANALYTIC_NAME,
                friends_bonus_gb: quota.space,
                referal_friends_invited: list.length,
            });
        }
    }, [isReturn]);

    const handleClose = useCallback(() => {
        if (!isReturn) {
            emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_MODAL_CLOSE);
            sendPaymentGa({
                action: 'close',
                eventCategory: ECategoryGa.referalModal,
                name: REFERRAL_ANALYTIC_NAME,
                friends_bonus_gb: quota.space,
                referal_friends_invited: list.length,
            });
        }
        onClose?.();
    }, [onClose, isReturn]);
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleClick = useCallback((info: BillingReferralProgram) => {
        emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_MODAL_CLICK);
        sendPaymentGa({
            action: 'click',
            eventCategory: ECategoryGa.referalModal,
            name: REFERRAL_ANALYTIC_NAME,
            button_name: 'referal_friend_info',
            referal_friend_id: info.owner.email,
            friends_bonus_gb: quota.space,
            referal_friends_invited: list.length,
        });
        closeReferralModal();
        renderReferralUserModal({ info });
    }, []);

    const header = useMemo(() => {
        return <div className={styles.header}>Бонусы за друзей</div>;
    }, []);

    const content = useMemo(
        () => (
            <Content isModal>
                <ReferralInfoBlock quota={quota} referralCount={list.length} isMaxQuota={isMaxQuota} />
                <ReferralLinkBlock quota={quota} isMaxQuota={isMaxQuota} />
                <ReferralUsersBlock list={list} onClick={handleClick} />
            </Content>
        ),
        [quota, list, isMaxQuota, isMobile]
    );

    const mobileContent = useMemo(
        () => (
            <Content isModal>
                <div className={styles.scrollContainer}>
                    <div className={classNames({ [styles.stickyHeader]: isMobile })}>
                        <ReferralInfoBlock isMobile quota={quota} referralCount={list.length} isMaxQuota={isMaxQuota} />
                        <ReferralLinkBlock isMobile quota={quota} isMaxQuota={isMaxQuota} />
                    </div>
                    <ReferralUsersBlock isMobile list={list} onClick={handleClick} />
                </div>
            </Content>
        ),
        [quota, list, isMaxQuota, isMobile]
    );

    if (isMobile) {
        return (
            <MobileDialog
                id="referrer-modal-phone"
                onClose={handleClose}
                closeOnDimmerClick
                mod="confirm"
                topmost
                title={header}
                scrollRef={scrollRef}
                className={styles.rootMobile}
            >
                {mobileContent}
            </MobileDialog>
        );
    }
    return (
        <Dialog open header={header} id="referrer-modal" onCancel={handleClose} className={styles.root}>
            {content}
        </Dialog>
    );
}

/* tempexp_18086-end */
